const sum = list => list.reduce((acc, next) => acc += (next || 0), 0)
let INPUT_DATA_KEYER = { id: 0 } // allows us to key and reuse DOM elements in the chart, but forces full re-render when input data changes

export default {
  name: 'bar-chart',

  props: {
    values: { type: Array, required: true},
    showStacked: { type: Boolean, default: true},
    showStackLegend: { type: Boolean, default: true},
    barColor: { type: String, default: '#2196f3'},
    stackColors: { type: Object },
    verticalLabels: { type: Boolean },
    minBarWidth: { type: Number, default: 6.5 }, // bar width - 2px of margin
    barHeight: { type: String },
  },

  data() {
  	return {
      INPUT_DATA_KEYER,
      stackFilter: null,
      activeBarIdx: null,
      chartWidth: null,
  	}
  },

  computed: {

    // True when `values` contains complex objects instead of simple numbers, bar chart will display object values as a stack
    isStackedValues() {
      return this.values.some(value => value !== null && typeof value === 'object')
    },

    chunkSize() {
      return this.chartWidth === null
        ? 1
        : Math.ceil( this.values.length / (this.chartWidth / this.minBarWidth) )
    },

    /**
     * Splits up the input array into chunks/sub-arrays of `chunkSize` lengths.
     * This allows us to accept input arrays of thousand of values, without having to render 1000's of bar's in the chart, which would be too small to see/interact with.
     */
  	chunked() {
      let chunked = []
      for (var i = 0; i < this.values.length; i+=this.chunkSize) {
        chunked.push( this.values.slice(i, i + this.chunkSize) )
      }
      return chunked
  	},

    /**
     * Computes an averaged value for each chunk that we use to render each bar
     */
    barValues() {
      return this.chunked
        .map(chunk => {
          if(this.isStackedValues) {
            if(chunk.every(stack => Object.values(stack).every(val => val === null))) // preserve entirely `null` chunks as `null`, not `0`, as these are rendered differently 
              return null
            else
              return sum(chunk.map(stack => sum(Object.values(stack)))) / chunk.length
          }
          else {
            if(chunk.every(val => val === null)) // preserve entirely `null` chunks as `null`, not `0`, as these are rendered differently 
              return null
            else
              return sum(chunk) / chunk.length
          }
        })
    },

    activeValueStartIndex() {
      return this.activeBarIdx != null ? this.activeBarIdx * this.chunkSize : null
    },

    activeValueEndIndex() {
      return this.activeBarIdx != null ? this.activeValueStartIndex + this.chunked[this.activeBarIdx].length - 1 : null
    },

    activeValue() {
      let activeValues = this.values.slice(this.activeValueStartIndex, this.activeValueEndIndex + 1)

      return this.isStackedValues
        ? sum(activeValues.map(stack => this.hasStackFilter ? stack[this.stackFilter] : sum(Object.values(stack))))
        : sum(activeValues)
    },

    rangeMax() {
      return Math.max(...this.barValues)
    },

    popUpLeft() {
      let barMargin = 2
      return `calc(${((this.activeBarIdx)/this.barValues.length + (0.5/this.barValues.length)) * 100}% - ${barMargin}px);`
    },

    hasStackFilter() {
      return this.stackFilter != null
    },

    uniqueStackKeys() {
      return Array.from(new Set( // dedupe
        this.values
          .map(stack => Object.keys(stack))
          .reduce((prev, curr) => prev.concat(curr), []) // flatten results
      )).sort()
    },

    stackColorsByKey() {
      if(this.stackColors) return this.stackColors
      let steps = this.uniqueStackKeys.length - 1
      return Object.fromEntries(this.uniqueStackKeys.map((key, idx) => [key,  `rgb(255, 0, ${((idx++)/steps) * 255})`]))
    },
  },

  watch: {
    values() {
      INPUT_DATA_KEYER.id++
      this.stackFilter = null
    },

    'chunked.length'() {
      this.activeBarIdx = null
    }
  },

  mounted() {
    this.getChatWidth()
    window.addEventListener('resize', this.getChatWidth)
  },

  methods: {
    getChatWidth(e) {
      this.chartWidth = this.$el.offsetWidth
    },

    /**
     * Merges stack objects for a chunk by summing the value for each unique key
     * Eg: Given the chunk [{a:1, b:1}, {a:1, b:2, c:1}] it will produce {a:2, b:3, c:1}
     */
    getAggregateStackForChunk(chunkIdx) {
      let chunk = this.chunked[chunkIdx]

      return chunk.reduce((acc, stack) => {
        Object.entries(stack).forEach(([key, value]) => {
          if(acc[key] == null) acc[key] = 0
          acc[key] += value
        })
        return acc
      }, {})

    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getChatWidth)
  },
}
